<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import AppIcon from '~/components/elements/AppIcon.vue'
import { useI18n, useLocalePath } from '#i18n'
import {
  type Language,
  type LanguageCode,
  supportedLanguages,
} from '~/models/Language.model'

interface Props {
  forceRefresh?: boolean
  dropDirection?: 'up' | 'down'
  languages?: LanguageCode[]
}

const props = withDefaults(defineProps<Props>(), {
  forceRefresh: false,
  dropDirection: 'down',
  languages: () => supportedLanguages.map((lang) => lang.code),
})

const { locale, setLocale: setI18nLocale } = useI18n()
const isOpen = ref(false)
const dropdownRef = ref<HTMLElement | null>(null)
const router = useRouter()
const localePath = useLocalePath()

const languages = computed(() =>
  supportedLanguages.filter((lang) => props.languages!.includes(lang.code))
)

const currentLanguage = computed(
  () =>
    languages.value.find((lang: Language) => lang.code === locale.value) ||
    languages.value[0]
)

const setLocale = async (langCode: LanguageCode) => {
  await setI18nLocale(langCode)
  isOpen.value = false

  const newPath = localePath(router.currentRoute.value.path)
  if (props.forceRefresh) {
    window.location.href = newPath
  } else {
    await router.push(newPath)
  }
}

const toggleDropdown = () => {
  isOpen.value = !isOpen.value
}

const handleClickOutside = (event: MouseEvent) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    isOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<template>
  <div ref="dropdownRef" class="relative" data-testid="language-switcher">
    <button
      class="group bg-white flex items-center space-x-2 rounded-lg border border-basics-border px-3 py-1.5 hover:bg-primary-50 focus:bg-primary-50 hover:border-primary focus:border-primary"
      @click="toggleDropdown"
    >
      <AppIcon
        :name="currentLanguage.flag"
        size="sm"
        :color="isOpen ? 'primary' : 'basics-text-secondary'"
      />
      <span class="ml-2 text-sm transition-colors duration-200">
        {{ currentLanguage.code.toUpperCase() }}
      </span>
      <AppIcon
        name="fa6-solid:angle-down"
        size="sm"
        color="basics-icon"
        class="transform transition-all duration-200"
        :class="{ 'rotate-180': isOpen }"
      />
    </button>
    <div
      v-if="isOpen"
      :class="[
        'bg-white absolute z-20 w-48 rounded-md py-2 shadow-xl first:py-0 last:py-0',
        props.dropDirection === 'up'
          ? 'bottom-full right-0 mb-2'
          : 'right-0 top-full mt-2',
      ]"
    >
      <button
        v-for="(lang, index) in languages"
        :key="lang.code"
        type="button"
        class="group flex w-full items-center space-x-3 px-4 py-2 text-left text-sm text-basics-text-secondary transition-colors duration-200 hover:bg-primary-50"
        :class="{
          'rounded-t-md': index === 0,
          'rounded-b-md': index === languages.length - 1,
        }"
        data-testid="language-switcher-language-button"
        @click.prevent="setLocale(lang.code)"
      >
        <AppIcon
          :name="lang.flag"
          size="sm"
          class="transition-colors duration-200 group-hover:translate-x-2"
        />
        <span
          class="text-base text-black transition-all duration-200 group-hover:translate-x-2 group-hover:font-bold"
          >{{ lang.name }}</span
        >
      </button>
    </div>
  </div>
</template>
